/**
 * Education & Experience timeline — UI logic (derivers, blank factories, flags).
 *
 * The data MODEL now lives in `shared-resources` (`oasis-timeline.types`); this
 * file holds the pure, Angular-free UI logic that operates on it:
 *   - chronology / gap / overlap derivers (spine-only, kind-agnostic)
 *   - typed blank-entry factories per kind
 *   - OARM review-flag derivation
 *
 * Dates stay raw ISO `YYYY-MM-DD` strings end-to-end (no Date/timezone
 * formatting) to avoid UTC/offset drift.
 */

import { programEligibilityThresholds } from '../../../../core/cycle/program-eligibility.rules';
import type {
	TimelineEntry,
	TimelineKind,
	TimelineGap,
	UndergradEntry,
	NonLegalGradEntry,
	InitialLegalDegreeEntry,
	GraduateLegalEntry,
	ClerkshipEntry,
	FellowshipEntry,
	IAdditionalLawSchool,
	ILawSchoolAttendance,
} from 'shared-resources';
import { US_LAW_SCHOOL_NOT_LISTED } from '../../../../core/reference-data/reference-data';

/** A detected overlap between two entries (UI-only, derived, never persisted). */
export interface TimelineOverlap {
	firstEntryId: string;
	secondEntryId: string;
}

/**
 * Type-safe selector: all entries of a given kind, narrowed to that kind's
 * concrete type. Preferred over ad-hoc `.filter()` so callers (Employment's
 * derived rows, the future reviewer/reporting side) get full typing for free.
 *
 *   entriesOfKind(entries, 'clerkship')  // → ClerkshipEntry[]
 *   entriesOfKind(entries, 'fellowship') // → FellowshipEntry[]
 */
export function entriesOfKind<K extends TimelineKind>(entries: readonly TimelineEntry[], kind: K): Extract<TimelineEntry, { kind: K }>[] {
	return entries.filter((e): e is Extract<TimelineEntry, { kind: K }> => e.kind === kind);
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// ── Eligibility rule thresholds (OARM, 2026) ─────────────────────────────────

/** Max allowed gap between two consecutive EPAs before OARM review. */
export const MAX_EPA_GAP_DAYS = 120;

/** Max allowed gap from the end of law education to the first EPA, in months. */
export const MAX_LAW_TO_EPA_GAP_MONTHS = 9;

/** An EPA graduate law degree (LLM) should run one academic year: 9–12 months. */
export const EPA_LLM_MIN_MONTHS = 9;
export const EPA_LLM_MAX_MONTHS = 12;

/**
 * True only for a fully-entered, well-formed `YYYY-MM-DD` with a plausible
 * 4-digit year. Guards against the intermediate values `<input type="date">`
 * emits while the year is typed digit-by-digit (0002, 0020, 0202…). Mirrors the
 * ARC case-form guard.
 */
export function isCompleteDate(value: string | null | undefined): boolean {
	if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
	const year = Number(value.slice(0, 4));
	if (year < 1900 || year > 2999) return false;
	return !Number.isNaN(new Date(value).getTime());
}

/** Parse an ISO date to time, or null when empty/incomplete. */
function toTime(iso: string): number | null {
	if (!isCompleteDate(iso)) return null;
	return Date.parse(iso);
}

/** Whole months between two dates, rounded up to the next full month (min 1). */
export function monthsBetween(fromIso: string, toIso: string): number {
	const a = toTime(fromIso);
	const b = toTime(toIso);
	if (a === null || b === null || b <= a) return 0;
	return Math.max(1, Math.ceil((b - a) / MS_PER_DAY / 30.4375));
}

/** Effective end time of an entry (now, for ongoing). */
function effectiveEnd(e: TimelineEntry): number | null {
	if (e.ongoing) return Date.now();
	return toTime(e.endDate);
}

/**
 * Sort entries chronologically by start then end date. Entries without a start
 * date sort to the end. Pure — returns a new array.
 */
export function sortTimeline(entries: readonly TimelineEntry[]): TimelineEntry[] {
	return [...entries].sort((a, b) => {
		const as = toTime(a.startDate);
		const bs = toTime(b.startDate);
		if (as === null && bs === null) return 0;
		if (as === null) return 1;
		if (bs === null) return -1;
		if (as !== bs) return as - bs;
		const ae = a.ongoing ? Number.MAX_SAFE_INTEGER : (toTime(a.endDate) ?? as);
		const be = b.ongoing ? Number.MAX_SAFE_INTEGER : (toTime(b.endDate) ?? bs);
		return ae - be;
	});
}

// ── Eligibility-preserving activities (EPAs) ─────────────────────────────────

/**
 * Whether a graduate law degree (LLM) counts as an eligibility-preserving
 * activity. Per OARM it qualifies only when it is a standalone, full-time
 * program taken AFTER the initial legal degree — i.e. not part of a joint
 * program, not concurrent with law school, and not part-time.
 *
 * The applicant's own status selection encodes all three exclusions, so it is
 * the primary source of truth. As a safety check we also reject an LLM whose
 * dates overlap the initial legal degree (concurrent study) even if the status
 * says otherwise.
 */
export function isEpaLlm(entry: GraduateLegalEntry, entries: readonly TimelineEntry[] = []): boolean {
	const gle = entry.graduateLegalEducation;
	if (gle.hasLlm !== 'yes') return false;
	// Only the two standalone, full-time, post-degree selections qualify.
	if (gle.statusSelection !== 'graduate_llm_after_jd' && gle.statusSelection !== 'graduate_llm_after_non_jd') return false;

	// Safety check: concurrent with the initial legal degree → not an EPA.
	const llmSpan = llmSpanOf(entry);
	if (llmSpan) {
		for (const other of entries) {
			if (other.kind !== 'initial_legal_degree') continue;
			const law = lawDegreeSpan(other);
			const lawStart = toTime(law.startDate);
			const lawEnd = toTime(law.endDate);
			if (lawStart === null || lawEnd === null) continue;
			if (llmSpan.start < lawEnd && lawStart < llmSpan.end) return false;
		}
	}
	return true;
}

/** The LLM's own span, derived from its attendance rows. */
function llmSpanOf(entry: GraduateLegalEntry): { start: number; end: number } | null {
	const { startDate, endDate } = deriveSpineFromAttendance(entry.graduateLegalEducation.attendance);
	const start = toTime(startDate);
	const end = toTime(endDate);
	if (start === null || end === null) return null;
	return { start, end };
}

/** Duration in months of an EPA LLM (0 when its dates aren't complete yet). */
export function epaLlmDurationMonths(entry: GraduateLegalEntry): number {
	const { startDate, endDate } = deriveSpineFromAttendance(entry.graduateLegalEducation.attendance);
	if (!isCompleteDate(startDate) || !isCompleteDate(endDate)) return 0;
	return monthsBetween(startDate, endDate);
}

/** Whether an EPA LLM's duration falls outside one academic year (9–12 months). */
export function isEpaLlmDurationOutOfRange(entry: GraduateLegalEntry, entries: readonly TimelineEntry[] = []): boolean {
	if (!isEpaLlm(entry, entries)) return false;
	const months = epaLlmDurationMonths(entry);
	if (months === 0) return false;
	return months < EPA_LLM_MIN_MONTHS || months > EPA_LLM_MAX_MONTHS;
}

/**
 * Every entry that counts as an eligibility-preserving activity: clerkships,
 * fellowships, and qualifying (standalone, full-time) graduate law degrees.
 */
export function epaEntries(entries: readonly TimelineEntry[]): TimelineEntry[] {
	return entries.filter((e) => {
		if (e.kind === 'clerkship' || e.kind === 'fellowship') return true;
		if (e.kind === 'graduate_legal') return isEpaLlm(e, entries);
		return false;
	});
}

/**
 * The date the applicant's law education ended: the initial legal degree's
 * conferral date, or — for joint-degree applicants earning a second degree
 * alongside the J.D. — the later of the two completion dates. Courses of study
 * may overlap and finish at different times, so we take the max.
 *
 * Returns '' when it can't be determined yet.
 */
export function lawEducationEndDate(entries: readonly TimelineEntry[]): string {
	const candidates: string[] = [];
	for (const e of entries) {
		if (e.kind === 'initial_legal_degree') {
			if (isCompleteDate(e.lawDegreeExpectedOrConferred)) candidates.push(e.lawDegreeExpectedOrConferred);
		}
		// Joint second degree earned alongside the J.D. — legal or non-legal.
		if (e.kind === 'non_legal_grad' && e.jointDegreeWithLaw) {
			if (isCompleteDate(e.endDate)) candidates.push(e.endDate);
		}
		if (e.kind === 'graduate_legal' && e.graduateLegalEducation.statusSelection === 'joint_jd_llm_current_student') {
			const { endDate } = deriveSpineFromAttendance(e.graduateLegalEducation.attendance);
			if (isCompleteDate(endDate)) candidates.push(endDate);
		}
	}
	if (!candidates.length) return '';
	return candidates.reduce((a, b) => (a >= b ? a : b));
}

/**
 * Months between the end of law education and the start of the FIRST EPA.
 * Returns 0 when either bound is unknown or there are no EPAs.
 */
export function lawToFirstEpaGapMonths(entries: readonly TimelineEntry[]): number {
	const lawEnd = lawEducationEndDate(entries);
	if (!isCompleteDate(lawEnd)) return 0;
	const epas = sortTimeline(epaEntries(entries)).filter((e) => isCompleteDate(epaStartDateOf(e)));
	if (!epas.length) return 0;
	const firstStart = epaStartDateOf(epas[0]);
	return monthsBetween(lawEnd, firstStart);
}

/** An EPA's start date (LLMs derive theirs from attendance). */
function epaStartDateOf(entry: TimelineEntry): string {
	if (entry.kind === 'graduate_legal') {
		return deriveSpineFromAttendance(entry.graduateLegalEducation.attendance).startDate;
	}
	return entry.startDate;
}

/** An EPA's end date (LLMs derive theirs from attendance; ongoing → ''). */
function epaEndDateOf(entry: TimelineEntry): string {
	if (entry.kind === 'graduate_legal') {
		return deriveSpineFromAttendance(entry.graduateLegalEducation.attendance).endDate;
	}
	return entry.endDate;
}

/**
 * Detect gaps of more than {@link MAX_EPA_GAP_DAYS} days between consecutive
 * ELIGIBILITY-PRESERVING ACTIVITIES. Education-to-education gaps are NOT
 * reported — per OARM, only EPA continuity matters.
 *
 * Preserves any explanation the applicant already typed (keyed by bounding ids).
 */
export function detectGaps(entries: readonly TimelineEntry[], existing: readonly TimelineGap[] = []): TimelineGap[] {
	const epas = epaEntries(entries);
	const sorted = sortTimeline(epas).filter((e) => toTime(epaStartDateOf(e)) !== null);
	const gaps: TimelineGap[] = [];
	let runningEnd: number | null = null;
	let runningEndEntry: TimelineEntry | null = null;

	for (const entry of sorted) {
		const start = toTime(epaStartDateOf(entry))!;
		if (runningEnd !== null && runningEndEntry && start - runningEnd > MAX_EPA_GAP_DAYS * MS_PER_DAY) {
			const fromIso = new Date(runningEnd).toISOString().slice(0, 10);
			const toIso = new Date(start).toISOString().slice(0, 10);
			const prior = existing.find((g) => g.afterEntryId === runningEndEntry!.id && g.beforeEntryId === entry.id);
			gaps.push({
				afterEntryId: runningEndEntry.id,
				beforeEntryId: entry.id,
				fromDate: fromIso,
				toDate: toIso,
				months: monthsBetween(fromIso, toIso),
				explanation: prior?.explanation ?? '',
			});
		}
		const end = entry.ongoing ? Date.now() : toTime(epaEndDateOf(entry));
		if (end !== null && (runningEnd === null || end > runningEnd)) {
			runningEnd = end;
			runningEndEntry = entry;
		}
	}
	return gaps;
}

/** Detect overlapping spans (two entries whose date ranges intersect). */
export function detectOverlaps(entries: readonly TimelineEntry[]): TimelineOverlap[] {
	const dated = entries.filter((e) => toTime(e.startDate) !== null);
	const overlaps: TimelineOverlap[] = [];
	for (let i = 0; i < dated.length; i++) {
		for (let j = i + 1; j < dated.length; j++) {
			const aStart = toTime(dated[i].startDate)!;
			const aEnd = effectiveEnd(dated[i]) ?? aStart;
			const bStart = toTime(dated[j].startDate)!;
			const bEnd = effectiveEnd(dated[j]) ?? bStart;
			if (aStart < bEnd && bStart < aEnd) {
				overlaps.push({ firstEntryId: dated[i].id, secondEntryId: dated[j].id });
			}
		}
	}
	return overlaps;
}

// ── Duration ─────────────────────────────────────────────────────────────────

/** Duration in months for an experience entry (0 until both dates are valid). */
export function durationMonthsFor(e: ClerkshipEntry | FellowshipEntry): number {
	if (e.ongoing || !isCompleteDate(e.startDate) || !isCompleteDate(e.endDate)) return 0;
	return monthsBetween(e.startDate, e.endDate);
}

// ── Timeline span derivation ─────────────────────────────────────────────────

/**
 * The law degree's span on the timeline.
 *
 * Starts at the earliest law school attended (a transfer student's first school,
 * otherwise the degree-granting school) and ends at the degree conferral date.
 * There is no separate attendance end date for the conferring school — conferral
 * IS its end.
 */
export function lawDegreeSpan(entry: InitialLegalDegreeEntry): { startDate: string; endDate: string } {
	const starts = [entry.degreeSchoolStartDate, ...entry.additionalLawSchools.map((s) => s.startDate)].filter(isCompleteDate);
	const startDate = starts.length ? starts.reduce((a, b) => (a <= b ? a : b)) : '';
	return { startDate, endDate: entry.lawDegreeExpectedOrConferred };
}

/**
 * Attendance-derived span for the graduate-legal (LLM) block, which still keeps
 * its dates as attendance rows. Start = earliest attendance start; End = latest
 * degree-conferred date (or latest attendance end when conferred is blank).
 * Returns '' for any bound that can't be determined yet, keeping the timeline's
 * sort/gap/overlap derivers fed without asking for dates already entered.
 */
export function deriveSpineFromAttendance(attendance: readonly ILawSchoolAttendance[]): {
	startDate: string;
	endDate: string;
} {
	const starts = attendance.map((a) => a.startDate).filter(isCompleteDate);
	const ends = attendance.map((a) => a.lawDegreeExpectedOrConferred || a.endDate).filter(isCompleteDate);
	const min = (xs: string[]): string => (xs.length ? xs.reduce((a, b) => (a <= b ? a : b)) : '');
	const max = (xs: string[]): string => (xs.length ? xs.reduce((a, b) => (a >= b ? a : b)) : '');
	return { startDate: min(starts), endDate: max(ends) };
}

// ── Per-entry validation (mirrors the old sections' isValid()) ───────────────

/**
 * Start present, and end present unless the entry is ongoing.
 *
 * `ongoing` is legacy — no card collects it any more (see the cleanup note on
 * `TimelineEntryBase.ongoing`). Kept here so stored entries that still carry it
 * validate the way they were saved rather than failing on load.
 */
function directDatesValid(e: { startDate: string; endDate: string; ongoing: boolean }): boolean {
	if (!isCompleteDate(e.startDate)) return false;
	return e.ongoing || isCompleteDate(e.endDate);
}

/**
 * A non-legal graduate degree needs both dates, unconditionally.
 *
 * OARM requires a completion date even while the applicant is still enrolled —
 * an expected date is a valid answer. It is not optional because that date feeds
 * the law-education end date (the later of the J.D. conferral and a joint
 * degree's completion) used by the `legal_education_epa_gap` rule, and it decides
 * joint-degree program eligibility. Left blank, the gap is measured from the
 * J.D. alone and can flag an applicant who has no gap.
 */
function nonLegalGradDatesValid(e: { startDate: string; endDate: string }): boolean {
	return isCompleteDate(e.startDate) && isCompleteDate(e.endDate);
}

/**
 * Attendance rows are complete. Used by the graduate-legal (LLM) block, which
 * still models its dates as attendance spans.
 */
function attendanceRowsValid(rows: readonly ILawSchoolAttendance[], min = 1): boolean {
	if (rows.length < min) return false;
	return rows.every((a) => isCompleteDate(a.startDate) && isCompleteDate(a.endDate) && isCompleteDate(a.lawDegreeExpectedOrConferred));
}

/** Every additional law school needs a name and a complete attended span. */
function additionalLawSchoolsValid(schools: readonly IAdditionalLawSchool[]): boolean {
	return schools.every((s) => s.schoolName.trim().length > 0 && isCompleteDate(s.startDate) && isCompleteDate(s.endDate));
}

function legalDegreeValid(e: InitialLegalDegreeEntry, slipPathways: boolean): boolean {
	const dc = e.degreeConferring;
	if (dc.path === 'us_jd') {
		if (!dc.schoolSelection) return false;
		if (dc.schoolSelection === US_LAW_SCHOOL_NOT_LISTED) {
			if (!dc.schoolName?.trim() || !dc.city?.trim() || !dc.stateTerritory?.trim()) return false;
		}
	} else if (dc.path === 'non_us_jd') {
		if (!dc.schoolName.trim() || !dc.city.trim() || !dc.country.trim()) return false;
	} else {
		if (!dc.schoolName.trim() || !dc.city.trim() || !dc.country.trim() || !dc.degreeType.trim() || !dc.barJurisdiction) return false;
	}
	// The degree-granting school's start date and the degree's conferral date are
	// the only dates this entry needs; any earlier schools carry their own span.
	if (!isCompleteDate(e.degreeSchoolStartDate) || !isCompleteDate(e.lawDegreeExpectedOrConferred)) return false;
	if (!additionalLawSchoolsValid(e.additionalLawSchools)) return false;
	const g = e.grades;
	if (!g.gpa.trim() || !g.gradeRange || !g.classRank) return false;
	// SLIP-Pathways applicants must answer the two required disclosures in the
	// SLIP-Pathways supplement (the checklist itself is optional).
	if (slipPathways) {
		const spa = e.slipPathwaysAchievement;
		if (!spa || !spa.hasFailingGrades || !spa.willingToWorkInDC) return false;
	}
	return true;
}

function graduateLegalValid(e: GraduateLegalEntry, entries: readonly TimelineEntry[]): boolean {
	const gle = e.graduateLegalEducation;
	// An explicitly-added graduate-legal entry must describe an LLM.
	if (gle.hasLlm !== 'yes') return false;
	const s = gle.school;
	if (!s) return false;
	if (s.path === 'us') {
		if (!s.schoolSelection) return false;
		if (s.schoolSelection === US_LAW_SCHOOL_NOT_LISTED) {
			if (!s.schoolName?.trim() || !s.city?.trim() || !s.stateTerritory?.trim()) return false;
		}
	} else {
		if (!s.schoolName.trim() || !s.city.trim() || !s.country.trim()) return false;
	}
	if (!gle.statusSelection) return false;
	if (!attendanceRowsValid(gle.attendance)) return false;
	// An EPA LLM outside one academic year must explain its duration. `entries` is
	// required here: whether the LLM counts as an EPA depends on whether its dates
	// overlap the initial legal degree, so without the full timeline the check
	// would demand an explanation for a concurrent LLM that is not an EPA at all.
	if (isEpaLlmDurationOutOfRange(e, entries) && !(e.reasonDurationOutOfRange ?? '').trim()) return false;
	return true;
}

function clerkshipValid(e: ClerkshipEntry): boolean {
	if (!directDatesValid(e)) return false;
	if (!e.courtType || !e.jobTitle) return false;
	if (!e.courtName.trim() || !e.city.trim() || !e.stateOrCountry.trim()) return false;
	const dur = durationMonthsFor(e);
	if (dur > 0 && dur < 12 && !(e.reasonUnder12Months ?? '').trim()) return false;
	return true;
}

function fellowshipValid(e: FellowshipEntry): boolean {
	if (!e.fellowshipType) return false;
	if (e.fellowshipType === 'doj_fellowship' && !e.dojFellowshipType) return false;
	if (e.fellowshipType !== 'doj_fellowship' && !(e.fellowshipName ?? '').trim()) return false;
	if (!directDatesValid(e)) return false;
	if (!e.city.trim() || !e.stateOrCountry.trim()) return false;
	const dur = durationMonthsFor(e);
	if (dur > 0 && dur < 12 && !(e.reasonUnder12Months ?? '').trim()) return false;
	return true;
}

/**
 * Whether a single timeline entry passes its kind-specific required-field checks.
 *
 * `entries` is the full timeline. The graduate-legal check needs it to decide
 * whether an LLM counts as an EPA (which depends on overlap with the law degree),
 * so callers should pass it; it defaults to just this entry so a lone-entry call
 * still behaves sensibly.
 */
export function isEntryValid(e: TimelineEntry, opts: { slipPathways?: boolean; entries?: readonly TimelineEntry[] } = {}): boolean {
	const entries = opts.entries ?? [e];
	switch (e.kind) {
		case 'undergrad':
			return !!e.schoolName.trim() && !!e.degreeType && directDatesValid(e);
		case 'non_legal_grad':
			if (!e.schoolName.trim() || !e.degreeType) return false;
			if (e.degreeType === 'other' && !(e.otherDegreeText ?? '').trim()) return false;
			return nonLegalGradDatesValid(e);
		case 'initial_legal_degree':
			return legalDegreeValid(e, opts.slipPathways ?? false);
		case 'graduate_legal':
			return graduateLegalValid(e, entries);
		case 'clerkship':
			return clerkshipValid(e);
		case 'fellowship':
			return fellowshipValid(e);
	}
}

/**
 * Detected gaps still missing the applicant's explanation.
 *
 * `epa_gap` is one of the four flags OARM requires an explanation for, alongside
 * the two under-12-month EPA flags and the LLM duration flag. Those three hang
 * off a single entry, so `isEntryValid` enforces them. A gap belongs to no entry
 * — it is the space between two — and its explanation lives in the separate
 * `gapExplanations` collection, so it has to be enforced at the section level.
 */
export function unexplainedGaps(gaps: readonly TimelineGap[]): TimelineGap[] {
	return gaps.filter((g) => !(g.explanation ?? '').trim());
}

// ── OARM review flags: intentionally NOT derived here ────────────────────────
//
// The API is the sole author of `reviewFlags`. This file used to hold a second
// implementation of those rules, which drifted from the server's: it raised a
// joint-degree flag the server did not, and its EPA duration checks disagreed
// with the server's on incomplete entries. Nothing applicant-facing ever read a
// flag, so the duplication bought nothing and made flags permanent — the client
// accumulated them and the server unioned them in, so no flag could retract.
//
// See `deriveReviewFlags` in doj_asb-cap_api/src/_shared/oasis/review-flags.ts.
// Everything a flag depends on is persisted on the application body, so there is
// nothing the client can see that the server cannot.

// ── J.D. recency hard stop (not a review flag) ────────────────────────────────

/**
 * The J.D. eligibility cutoff for a cycle.
 *
 * `servedCutoff` is the value the server resolved and published in its config
 * document — the per-cycle override when OARM set one, otherwise October 1 of
 * (cycleYear − 4). Pass it whenever it is available.
 *
 * Without it this derives the standing October 1 rule, which is a FALLBACK for a
 * failed config fetch and not the intended path: the server re-checks with its own
 * resolved cutoff at submit, so a client that derives while the server overrides is
 * exactly the divergence the config endpoint exists to remove.
 *
 * Delegates to the shared eligibility rules so the date has ONE derivation —
 * Program Eligibility enforces the same stop against its own graduation date, and
 * the two screens must never disagree about where the boundary is.
 */
export function jdEligibilityCutoff(cycleYear: number, servedCutoff?: string): string {
	return programEligibilityThresholds(cycleYear, servedCutoff).tooSeniorBefore;
}

/**
 * Whether the initial legal degree's conferral date is before the cycle cutoff.
 * Only a real, complete conferral date can trip this — an expected future date
 * never will. Drives the hard stop on the Law Degree card (warning + disabled
 * Save & Continue) and is re-checked server-side at submit.
 */
export function isJdTooSenior(entry: InitialLegalDegreeEntry, cycleYear: number, servedCutoff?: string): boolean {
	// Only J.D. paths are subject to the recency rule.
	if (entry.degreeConferring.path === 'non_jd') return false;
	const conferred = entry.lawDegreeExpectedOrConferred;
	if (!isCompleteDate(conferred)) return false;
	return conferred < jdEligibilityCutoff(cycleYear, servedCutoff);
}

// ── Typed blank-entry factories ──────────────────────────────────────────────

let seq = 0;
const nextId = (): string => `t-${Date.now().toString(36)}-${++seq}`;

function blankSpine(): { id: string; startDate: string; endDate: string; ongoing: boolean; note: string } {
	return { id: nextId(), startDate: '', endDate: '', ongoing: false, note: '' };
}

export function blankEntry(kind: TimelineKind): TimelineEntry {
	switch (kind) {
		case 'undergrad':
			return {
				...blankSpine(),
				kind,
				schoolName: '',
				location: { city: '', stateOrCountry: '', country: '' },
				degreeType: '',
				majors: [],
				graduationHonors: '',
			} satisfies UndergradEntry;
		case 'non_legal_grad':
			return {
				...blankSpine(),
				kind,
				schoolName: '',
				location: { city: '', stateOrCountry: '', country: '' },
				degreeType: '',
				otherDegreeText: '',
				majors: [],
				graduationHonors: '',
				jointDegreeWithLaw: false,
			} satisfies NonLegalGradEntry;
		case 'initial_legal_degree':
			return {
				...blankSpine(),
				kind,
				hasJd: 'yes',
				degreeConferring: { path: 'us_jd', schoolSelection: '' },
				degreeSchoolStartDate: '',
				lawDegreeExpectedOrConferred: '',
				additionalLawSchools: [],
				grades: { gpa: '', gradeRange: '', classRank: '' as InitialLegalDegreeEntry['grades']['classRank'] },
			} satisfies InitialLegalDegreeEntry;
		case 'graduate_legal':
			return {
				...blankSpine(),
				kind,
				graduateLegalEducation: {
					hasLlm: 'yes',
					school: { path: 'us', schoolSelection: '' },
					attendance: [{ startDate: '', endDate: '', lawDegreeExpectedOrConferred: '' }],
				},
			} satisfies GraduateLegalEntry;
		case 'clerkship':
			return {
				...blankSpine(),
				kind,
				durationMonths: 0,
				workSchedule: 'full_time',
				courtType: '',
				courtName: '',
				city: '',
				stateOrCountry: '',
				jobTitle: '',
				judgeName: '',
				judgeTitle: '',
				// Blank, not 'paid' — a default here asserted an answer the applicant never
				// gave, and it reaches OARM as a badge indistinguishable from a real one.
				payment: '',
				dutiesResponsibilities: '',
			} satisfies ClerkshipEntry;
		case 'fellowship':
			return {
				...blankSpine(),
				kind,
				durationMonths: 0,
				workSchedule: 'full_time',
				// See the clerkship note above.
				payment: '',
				fellowshipType: '',
				fellowshipName: '',
				sponsorOrganization: '',
				sponsorIsLawFirm: 'no',
				missionPurpose: '',
				eligibilityUrl: '',
				attachments: [],
				salaryStipend: '',
				city: '',
				stateOrCountry: '',
				supervisorName: '',
				supervisorTitle: '',
				dutiesResponsibilities: '',
			} satisfies FellowshipEntry;
	}
}
